/**
 * DataTable — Sprint 13 S13-29.
 *
 * Componentes "slot" (Table/THead/TBody/Tr/Th/Td) que estilizan
 * consistentemente las tablas del sa. Mantienen la flexibilidad de
 * los <th>/<td> nativos (cualquier ReactNode adentro) pero unifican
 * el aspecto: borde sutil, header bg-bg-sunken, hover por fila,
 * tipografia consistente.
 *
 * Reemplaza la repeticion de classNames en cada tabla.
 */
import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react';

interface TableProps {
  children: ReactNode;
  className?: string;
}

export function Table({ children, className = '' }: TableProps) {
  return (
    <div
      className={`bg-bg-card border-[0.5px] border-white/[0.08] rounded-md overflow-hidden ${className}`.trim()}
    >
      <table className="w-full">{children}</table>
    </div>
  );
}

/**
 * THead emite su propio `<tr>`: los hijos son los `<Th>` DIRECTAMENTE.
 *
 *   <THead><Th>Casa</Th><Th>Estado</Th></THead>   ✅
 *   <THead><Tr><Th>Casa</Th></Tr></THead>          ❌
 *
 * Envolver en `<Tr>` anida dos `<tr>`, que es HTML invalido: el navegador
 * los separa y queda una fila vacia quedandose con el fondo del encabezado
 * mientras los titulos caen a la fila siguiente, sin fondo. Se veia como
 * columnas corridas. Corregido el 2026-09-02 en EntesExternos (x2) y
 * Deploys, que eran los tres casos que quedaban.
 */
export function THead({ children }: { children: ReactNode }) {
  return (
    <thead>
      <tr className="border-b-[0.5px] border-white/15 bg-bg-sunken">
        {children}
      </tr>
    </thead>
  );
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody>{children}</tbody>;
}

interface TrProps {
  children: ReactNode;
  hover?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function Tr({
  children,
  hover = true,
  className = '',
  'data-testid': testId,
}: TrProps) {
  const hoverClass = hover ? 'hover:bg-bg-elevated/50 transition-colors' : '';
  return (
    <tr
      className={`border-b-[0.5px] border-white/[0.08] ${hoverClass} ${className}`.trim()}
      data-testid={testId}
    >
      {children}
    </tr>
  );
}

type Align = 'left' | 'right' | 'center';

interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  align?: Align;
  children?: ReactNode;
}

export function Th({ align = 'left', className = '', children, ...rest }: ThProps) {
  const alignClass = {
    left: 'text-left',
    right: 'text-right',
    center: 'text-center',
  }[align];
  return (
    <th
      className={`${alignClass} px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium ${className}`.trim()}
      {...rest}
    >
      {children}
    </th>
  );
}

interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: Align;
  mono?: boolean;
  /** Color preset para el contenido. Default `secondary`. */
  tone?: 'primary' | 'secondary' | 'muted' | 'success' | 'warning' | 'danger';
  children: ReactNode;
}

export function Td({
  align = 'left',
  mono = false,
  tone = 'secondary',
  className = '',
  children,
  ...rest
}: TdProps) {
  const alignClass = {
    left: 'text-left',
    right: 'text-right',
    center: 'text-center',
  }[align];
  const toneClass = {
    primary: 'text-text-primary',
    secondary: 'text-text-secondary',
    muted: 'text-text-muted',
    success: 'text-state-success',
    warning: 'text-state-warning',
    danger: 'text-state-danger',
  }[tone];
  const monoClass = mono ? 'font-mono' : '';
  return (
    <td
      className={`${alignClass} px-4 py-3 text-sm ${toneClass} ${monoClass} ${className}`.trim()}
      {...rest}
    >
      {children}
    </td>
  );
}
